import { CalendarItemType, MealType } from '@akin-home/amplify';
import { RecurrenceMode } from '@akin-home/recurrence';
import { z } from 'zod';
import {
  CreateChecklistCommand,
  CreateGoalCommand,
  RefetchStateVariablesCommand,
} from './schemas';

export enum CommandTypes {
  /** General input commands */
  REPEAT_WATSON_MESSAGE = 'repeat_watson_message',
  REPEAT_USER_MESSAGE = 'repeat_user_message',
  ACTIVATE_MIC = 'activate_mic',

  FOLLOW_UP_INPUT = 'follow_up_input',

  /** Scheduled message commands */
  DELAY_SCHEDULED_MESSAGE = 'delay_scheduled_message',
  ANSWER_SCHEDULED_MESSAGE = 'answer_scheduled_message',

  /** Calendar Commands */
  OPEN_CALENDAR_ITEM = 'open_calendar_item',
  CHECK_CALENDAR_ITEMS = 'check_calendar_items',
  SHOW_CALENDAR_ITEM = 'show_calendar_item',
  SHOW_CALENDAR_ITEMS_TODAY = 'show_calendar_items_today',
  SHOW_CALENDAR_ITEMS = 'show_calendar_items',
  DELETE_CALENDAR_ITEM = 'delete_calendar_item',
  ADD_CALENDAR_ITEM = 'add_calendar_item',
  UPDATE_CALENDAR_ITEM = 'update_calendar_item',

  /** Recipe Comands */
  SHOW_RECIPE_ITEM = 'show_recipe_item',
  SHOW_QUICK_RECIPE_ITEM = 'show_quick_recipe_item',
  SHOW_EASY_RECIPE_ITEM = 'show_easy_recipe_item',

  /** Navigation command */
  NAVIGATE = 'navigate',
  GO_BACK = 'go_back',

  /** Record Commands */
  SAVE = 'save',
  DELETE = 'delete',
  UPDATE_FIELD = 'update_field',

  /** Timer command */
  TIMER_SET = 'timer_set',
  TIMER_PAUSE = 'timer_pause',
  TIMER_RESUME = 'timer_resume',
  TIMER_RESET = 'timer_reset',
  TIMER_STOP = 'timer_stop',

  /** Avatar commands */
  AVATAR_SET_EXPRESSION = 'set_expression',
  MINIMISE_AVATAR = 'minimise_avatar',
  MAXIMISE_AVATAR = 'maximise_avatar',
  HIDE_AVATAR = 'hide_avatar',
  SHOW_AVATAR = 'show_avatar',

  /** Checklist Commands */
  SHOW_CHECKLIST = 'show_checklist',
  DELETE_CHECKLIST = 'delete_checklist',
  ADD_LIST_ITEM = 'add_checklist_item',
  DELETE_LIST_ITEM = 'delete_checklist_item',

  MOVE_LIST_ITEM = 'move_list_item',

  SHOW_WEATHER = 'show_weather',

  SHOW_WEATHER_HOURLY = 'show_weather_hourly',
  WEATHER_TODAY_LOCAL = 'weather_today_local',
  ALL_WEATHER_HOURLY = 'all_weather_hourly',
  WEATHER_7_DAY = 'weather_7_day_local',

  CREATE_GOAL = 'create_goal',
  UPDATE_GOAL = 'update_goal',

  /** Phone command */
  PHONE = 'phone',
  CALL_CONTACT = 'call_contact',
  ADD_CONTACT = 'add_contact',

  /** App Commands */
  OPEN_APP = 'open_app',

  /** Time display */
  GET_TIME = 'get_time_location',

  WAKE_WORD = 'wake_word',

  SET_BRIGHTNESS = 'set_brightness',

  OPEN_SYSTEM_SETTINGS = 'open_system_settings',

  REFETCH_STATE_VARIABLES = 'refetch_state_variables',

  OPEN_FOCUSED_CHAT = 'open_focused_chat',
  CLOSE_FOCUSED_CHAT = 'close_focused_chat',

  // BMC Specific Displays
  DISPLAY_EMOTION_PICKER = 'display_emotion_picker',
  DISPLAY_EMOTION_SCALE = 'display_emotion_scale',
  DISPLAY_SCALE_OPTIONS = 'display_scale_options',

  // Voice Commands
  UPDATE_VOICE = 'update_voice',
}

export interface CommandBase {
  type: CommandTypes;
  data?: any;
}

export interface RepeatAIMessageCommand extends CommandBase {
  type: CommandTypes.REPEAT_WATSON_MESSAGE;
}

export interface RepeatUserMessageCommand extends CommandBase {
  type: CommandTypes.REPEAT_USER_MESSAGE;
}

export interface ActivateMicrophoneCommand extends CommandBase {
  type: CommandTypes.ACTIVATE_MIC;

  data?: {
    delay?: string;
  };
}

export interface PhoneCommand extends CommandBase {
  type: CommandTypes.PHONE;

  data: {
    number: string;
    mode: 'dial' | 'instant_call';
    contact?: {
      name?: string;
      number?: string;
    };
    delay?: string;
  };
}

/** The command calls a contact with a given name or relationship */
export interface CallContactCommand extends CommandBase {
  type: CommandTypes.CALL_CONTACT;

  data: {
    /** The name of the contact */
    name?: string;
    /** The relationship of the contact */
    relationship?: string;
    /** Message if command is successful */
    success_message?: string;
    /** Message if no contact was found */
    not_found_message?: string;
    /** Message if no number assigned to the contact */
    no_number_message?: string;
    /** Optional delay before phone is called */
    delay?: string;
  };
}

export interface AddContactCommand extends CommandBase {
  type: CommandTypes.ADD_CONTACT;

  data: {
    /** The name of the contact */
    name?: string;
    phone_number?: string;
    email?: string;
    /** The relationship of the contact */
    relationship?: string;
    /** Message if command is successful */
    success_message?: string;
    /** Message if no contact was found */
    error_message?: string;
  };
}

export interface FollowUpInputCommand extends CommandBase {
  type: CommandTypes.FOLLOW_UP_INPUT;

  data: {
    input_type: 'label' | 'text';
    input: string;
    delay?: string;
  };
}

/** Calendar Item Related Commands */
export interface ShowCalendarItemData {
  type?: CalendarItemType;
  title?: string;
  date?: string;
  mealType?: MealType;
  mode?: 'first' | 'all';
  number?: string;
  time?: string;
}
export interface ShowCalendarItemCommand extends CommandBase {
  type: CommandTypes.SHOW_CALENDAR_ITEM;
  data: ShowCalendarItemData;
}

export interface ShowCalendarItemsTodayCommand extends CommandBase {
  type: CommandTypes.SHOW_CALENDAR_ITEMS_TODAY;
  data: ShowCalendarItemData;
}
export interface ShowCalendarItemsCommand extends CommandBase {
  type: CommandTypes.SHOW_CALENDAR_ITEMS;
  data: ShowCalendarItemData;
}

export const OpenCalendarItemCommand = z.object({
  type: z.literal(CommandTypes.OPEN_CALENDAR_ITEM),
  data: z.object({
    type: z.nativeEnum(CalendarItemType),
    id: z.string(),
    in_edit_mode: z.coerce
      .string()
      .toLowerCase()
      .optional()
      .default('false')
      .transform((value) => value === 'true'),
    focused_field: z.string().optional(),
    error_message: z.string().optional(),
    none_found_message: z.string().optional(),
    success_message: z.string().optional(),
  }),
});

export type OpenCalendarItemCommand = z.infer<typeof OpenCalendarItemCommand>;

export const CheckCalendarItemsCommand = z.object({
  type: z.literal(CommandTypes.CHECK_CALENDAR_ITEMS),
  data: z.object({
    type: z.string().optional(),
    title: z.string().optional(),
    date: z.string().optional(),
    time: z.string().optional(),
    until_time: z.string().optional(),
    next_item: z.coerce.boolean().optional(),
    error_message: z.string().optional(),
    none_found_message: z.string().optional(),
    success_message: z.string().optional(),
  }),
});

export type CheckCalendarItemsCommand = z.infer<
  typeof CheckCalendarItemsCommand
>;

export const DeleteCalendarItemCommand = z.object({
  type: z.literal(CommandTypes.DELETE_CALENDAR_ITEM),
  data: z.object({
    type: z.string().optional(),
    id: z.string().optional(),
    title: z.string().optional(),
    match_confidence: z.coerce.number().optional(),
    start_date: z.string().optional(),
    start_time: z.string().optional(),
    not_found_message: z.string().optional(),
  }),
});

export type DeleteCalendarItemCommand = z.infer<
  typeof DeleteCalendarItemCommand
>;

export const AddCalendarItem = z.object({
  type: z.literal(CommandTypes.ADD_CALENDAR_ITEM),

  data: z.object({
    start_date: z.string().optional(),
    start_time: z.string().optional(),
    end_date: z.string().optional(),
    end_time: z.string().optional(),
    type: z
      .nativeEnum(CalendarItemType)
      .optional()
      .default(CalendarItemType.Event),
    title: z.string().optional(),
    meal_type: z.string().optional(),

    recurrence_mode: z.nativeEnum(RecurrenceMode).optional(),
    recurrence_interval: z.coerce.number().default(1),
    recurrence_days: z.string().optional(),
    recurrence_count: z.coerce.number().optional(),
    recurrence_until: z.string().optional(),
  }),
});

export type AddCalendarItem = z.infer<typeof AddCalendarItem>;

/** Timer Commands */
export interface TimerSetItem extends CommandBase {
  type: CommandTypes.TIMER_SET;
  data: {
    unit?: string;
    interval?: string;
  };
}

export interface ShowTimeCommand extends CommandBase {
  type: CommandTypes.GET_TIME;
  data: {
    Location: any;
    Date: any;
    Time: any;
  };
}

export interface DeleteCommand extends CommandBase {
  type: CommandTypes.DELETE;
  data: {
    success_message?: string;
    error_message?: string;
  };
}

export interface SaveCommand extends CommandBase {
  type: CommandTypes.SAVE;
  data: {
    success_message?: string;
    error_message?: string;
  };
}

export interface UpdateFieldCommand extends CommandBase {
  type: CommandTypes.UPDATE_FIELD;
  data: {
    field_name: string;
    value: string;
    success_message?: string;
    error_message?: string;
  };
}

export interface UpdateCalendarItem extends CommandBase {
  type: CommandTypes.UPDATE_CALENDAR_ITEM;
  data: {
    from?: string;
    to?: string;
  };
}

export interface ShowRecipeItemCommand extends CommandBase {
  type: CommandTypes.SHOW_RECIPE_ITEM;
  data: {
    tag?: string;
    tag2?: string;
    meal_type?: string;
  };
}

export interface ShowQuickRecipeItemCommand extends CommandBase {
  type: CommandTypes.SHOW_QUICK_RECIPE_ITEM;
  data: {
    tag?: string;
    tag2?: string;
    meal_type?: string;
  };
}

export interface ShowEasyRecipeItemCommand extends CommandBase {
  type: CommandTypes.SHOW_EASY_RECIPE_ITEM;
  data: {
    tag?: string;
    tag2?: string;
    meal_type?: string;
  };
}

export interface NavigateCommand {
  type: CommandTypes.NAVIGATE;

  data: {
    to: 'Calendar' | 'Chat';
    date?: string;
    params?: object;
  };
}

export interface GoBackCommand {
  type: CommandTypes.GO_BACK;
}

export interface AvatarExpressionCommands extends CommandBase {
  type: CommandTypes.AVATAR_SET_EXPRESSION;
  data: {
    expression: string;
  };
}

export interface MinimiseAvatarCommand extends CommandBase {
  type: CommandTypes.MINIMISE_AVATAR;
}

export interface MaximiseAvatarCommand extends CommandBase {
  type: CommandTypes.MAXIMISE_AVATAR;
}

export interface HideAvatarCommand extends CommandBase {
  type: CommandTypes.HIDE_AVATAR;
}

export interface ShowAvatarCommand extends CommandBase {
  type: CommandTypes.SHOW_AVATAR;
}

export interface MoveChecklistItemCommandData {
  webhook?: { source?: string; target?: string; item?: string };
}

export interface MoveChecklistItemCommandData {
  error: string;
  from_list: string;
  item: string;
  not_found_list: string;
  not_found_item?: string;
  to_list: string;
  type: string;
}

export interface MoveChecklistItemCommand extends CommandBase {
  type: CommandTypes.MOVE_LIST_ITEM;
  data: MoveChecklistItemCommandData;
}

export const ShowChecklistCommand = z.object({
  type: z.literal(CommandTypes.SHOW_CHECKLIST),
  data: z.object({
    id: z.string().optional(),
    title: z.string().optional(),
    confidence: z.string().optional(),
    not_found_message: z.string().optional(),
  }),
});

export type ShowChecklistCommand = z.infer<typeof ShowChecklistCommand>;

export type CreateChecklistCommand = z.infer<typeof CreateChecklistCommand>;

export const DeleteChecklistCommand = z.object({
  type: z.literal(CommandTypes.DELETE_CHECKLIST),
  data: z.object({
    id: z.string().optional(),
    title: z.string().optional(),
    confidence: z.string().optional(),
    not_found_message: z.string().optional(),
  }),
});

export type DeleteChecklistCommand = z.infer<typeof DeleteChecklistCommand>;

export const AddChecklistItemCommand = z.object({
  type: z.literal(CommandTypes.ADD_LIST_ITEM),
  data: z.object({
    checklist_id: z.string().optional(),
    checklist_title: z.string().optional(),
    item_text: z.string(),
    confidence: z.string().optional(),
    not_found_message: z.string().optional(),
    success_message: z.string().optional(),
  }),
});

export type AddChecklistItemCommand = z.infer<typeof AddChecklistItemCommand>;

export const DeleteChecklistItemCommand = z.object({
  type: z.literal(CommandTypes.DELETE_LIST_ITEM),
  data: z.object({
    checklist_id: z.string().optional(),
    checklist_title: z.string().optional(),
    confidence: z.string().optional(),

    item_text: z.string(),
    item_confidence: z.string().optional(),
    not_found_message: z.string().optional(),
  }),
});

export type DeleteChecklistItemCommand = z.infer<
  typeof DeleteChecklistItemCommand
>;

export const ShowWeatherCommand = z.object({
  type: z.literal(CommandTypes.SHOW_WEATHER),
  data: z.object({
    location: z.string().optional(),
    date: z.string().optional(),
  }),
});

export type ShowWeatherCommand = z.infer<typeof ShowWeatherCommand>;

export interface UpdateGoalCommand extends CommandBase {
  type: CommandTypes.UPDATE_GOAL;

  data: {
    /** Title */
    title?: string;

    /** The special goal reference used to identify the goal */
    reference_code?: string;
    /** How much to update the goal */
    count?: number;

    /** An optional message to post once the goal has been updated */
    success_message?: string;
  };
}

export type CreateGoalCommand = z.infer<typeof CreateGoalCommand>;

export interface OpenAppCommand extends CommandBase {
  type: CommandTypes.OPEN_APP;

  data: {
    /** Trying to open app by the app's label */
    label?: string;

    /** Trying to open the app by app package name */
    package_name?: string;

    /** Message if app is successfully opened */
    success_message?: string;

    /** Message if app was not found on the device */
    not_found?: string;

    /** Message if there was an error opening the app */
    error?: string;
  };
}

export interface SetWakeWord extends CommandBase {
  type: CommandTypes.WAKE_WORD;

  data: {
    active?: 'true' | 'false';
  };
}

export const OpeFocusedChatCommand = z.object({
  type: z.literal('open_focused_chat'),
  data: z.object({
    initial_label_prompt: z.string().optional(),
  }),
});

export type OpenFocusedChatCommand = z.infer<typeof OpeFocusedChatCommand>;

export const CloseFocusedChatCommand = z.object({
  type: z.literal('close_focused_chat'),
  data: z.object({
    after_close_label: z.string().optional(),
  }),
});

export type CloseFocusedChatCommand = z.infer<typeof CloseFocusedChatCommand>;

export const DisplayEmotionPickerCommand = z.object({
  type: z.literal('display_emotion_picker'),
  data: z.object({
    submission_input: z.string(),

    other_emotion_label: z.string().optional(),
    other_emotion_input: z.string().optional(),

    show_emotion_help: z.coerce.boolean().optional().default(true),
  }),
});

export type DisplayEmotionPickerCommand = z.infer<
  typeof DisplayEmotionPickerCommand
>;

export const DisplayScaleOptionsCommand = z.object({
  type: z.literal('display_scale_options'),
});

export type DisplayScaleOptionsCommand = z.infer<
  typeof DisplayScaleOptionsCommand
>;

export const UpdateVoiceCommand = z.object({
  type: z.literal('update_voice'),
  data: z.object({
    voice_enabled: z.coerce.boolean().optional(),
  }),
});
export type UpdateVoiceCommand = z.infer<typeof UpdateVoiceCommand>;

export type ChatCommand =
  | PhoneCommand
  | FollowUpInputCommand
  | ShowCalendarItemCommand
  | ShowCalendarItemsCommand
  | ShowCalendarItemsTodayCommand
  | NavigateCommand
  | DeleteCalendarItemCommand
  | AddCalendarItem
  | UpdateCalendarItem
  | AvatarExpressionCommands
  | MinimiseAvatarCommand
  | MaximiseAvatarCommand
  | HideAvatarCommand
  | ShowAvatarCommand
  | CreateChecklistCommand
  | AddChecklistItemCommand
  | DeleteChecklistItemCommand
  | DeleteChecklistCommand
  | MoveChecklistItemCommand
  | ShowChecklistCommand
  | ShowWeatherCommand
  | UpdateGoalCommand
  | CallContactCommand
  | ShowTimeCommand
  | RepeatAIMessageCommand
  | RepeatUserMessageCommand
  | ShowRecipeItemCommand
  | ShowQuickRecipeItemCommand
  | ShowEasyRecipeItemCommand
  | OpenAppCommand
  | SetWakeWord
  | ActivateMicrophoneCommand
  | AddContactCommand
  | GoBackCommand
  | SaveCommand
  | CreateGoalCommand
  | RefetchStateVariablesCommand
  | OpenFocusedChatCommand
  | CloseFocusedChatCommand
  | DisplayEmotionPickerCommand
  | DisplayScaleOptionsCommand
  | UpdateVoiceCommand
  | CheckCalendarItemsCommand;

/** Chat commands specific to calendar items */
export type CalendarItemChatCommand =
  | ShowCalendarItemCommand
  | ShowCalendarItemsCommand
  | ShowCalendarItemsTodayCommand
  | DeleteCalendarItemCommand
  | AddCalendarItem
  | UpdateCalendarItem;
